import React, { ReactElement } from "react";
import { Theme, useTheme } from "../../../context/theme/theme";
import { cn } from "../../../utils/shorthands";
import * as scss from "./icons.scss";

export type IconProps = {
	/** A icon to be cloned and applied relevant styling to */
	children: ReactElement<IconElementProps>;
	/** Controls the width and height of the component, defaults to `"large"` */
	size?: "small" | "medium" | "large" | "responsive";
	/** How many degrees the icon should be rotated, will be animated if changed from one value to another */
	rotation?: Rotation;
	/** If the icon should have another color */
	fill?: string;
	className?: string;
};

type Rotation = "0" | "90" | "180" | "270";

export const GOOGLE_MATERIAL_VIEWBOX = "0 -960 960 960";
export const AI_STAR_VIEWBOX = "0 0 32 32";

export function Icon({
	children,
	size = "large",
	rotation,
	fill,
	className,
}: IconProps): ReactElement {
	return React.cloneElement(children, {
		className: cn(
			"fancy-icon",
			scss[size],
			rotation !== undefined && degToClass(rotation),
			children.props.className,
			className
		),
		fill,
	});
}

export type IconDefinitionProps = {
	element: JSX.Element;
	viewBox?: string;
};

export type ThemeableIcon = {
	default: IconDefinitionProps;
} & Partial<Record<Theme, IconDefinitionProps>>;

/** Helper to create SVG components with correct outer markup and themed variant */
export function svgIcon(
	icon: JSX.Element | ThemeableIcon
): (props: IconElementProps) => JSX.Element {
	return ({ className, fill }) => {
		const normalizedIconSet = React.isValidElement(icon)
			? { default: { element: icon } }
			: (icon as ThemeableIcon);

		const theme = useTheme();
		const themedIcon = normalizedIconSet[theme] ?? normalizedIconSet.default;

		const viewBox = themedIcon.viewBox ?? "0 0 24 24";
		const element = themedIcon.element;

		return (
			<svg
				data-component="icon"
				data-fancy-icon-theme={theme}
				className={className}
				style={{ fill }}
				viewBox={viewBox}
				focusable="false"
				aria-hidden="true"
				fill="currentColor"
			>
				{element}
			</svg>
		);
	};
}

function degToClass(rotation: Rotation) {
	switch (rotation) {
		case "0":
			return scss.deg0;
		case "90":
			return scss.deg90;
		case "180":
			return scss.deg180;
		case "270":
			return scss.deg270;
	}
}

export type IconElementProps = {
	className?: string;
	fill?: string;
};
